import { useState } from 'react';

import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { Box, Dialog, DialogContent, Grid, IconButton, Stack, Typography } from '@mui/material';
import { deleteMcc, deleteMccVariables } from 'types/graphql';

import { Link, navigate, routes } from '@redwoodjs/router';
import { MetaTags, useMutation, useQuery } from '@redwoodjs/web';
import { toast } from '@redwoodjs/web/toast';

import MainCard from 'src/components/MainCard/MainCard';
import ModalContent from 'src/components/ModalContent/ModalContent';
import { showErrorToast, showSuccessToast } from 'src/components/Toasts/toast';
import { DELETE_MCC_MUTATION } from 'src/graphql/mutations/mccs.mutation';
import { GET_MCC } from 'src/graphql/queries/mccs.query';
import { PageContent } from 'src/layouts/MainLayout/PageContent';

import { TextEditorValues } from '../MccPage/MccPage';

type ViewMCCProps = {
    id: number;
};

const ViewMccPage = ({ id }: ViewMCCProps) => {
    const [openDeleteModal, setOpenDeleteModal] = useState(false);

    const { loading, error, data } = useQuery(GET_MCC, { variables: { id } });

    const [deleteMccRecord] = useMutation<deleteMcc, deleteMccVariables>(DELETE_MCC_MUTATION, {
        onCompleted: (data) => {
            showSuccessToast(`${data.deleteMcc.code} deleted successfully`);
            navigate('/mccs');
        },
        onError: (error) => {
            showErrorToast(error.message ?? 'Error Deleting the MCC');
        },
    });

    if (loading) return <>Loading...</>;
    if (error) return <>{toast.error(`Error! ${error.message}`)}</>;

    const { mcc } = data;

    const handleOpenDeleteModal = () => {
        setOpenDeleteModal(true);
    };

    const handleCloseDeleteModal = () => {
        setOpenDeleteModal(false);
    };

    const handleDeleteRecord = () => {
        deleteMccRecord({ variables: { id } });
    };

    return (
        <PageContent heading="View Merchant Category Code">
            <MetaTags title="ViewMcc" description="ViewMcc page" />
            <Stack spacing={5}>
                <MainCard boxShadow>
                    <Box
                        sx={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            justifyContent: 'space-between',
                        }}
                    >
                        <h2 className="font-normal mb-0 mt-10">MCC Details</h2>
                        <Box>
                            <Link to={routes.editMcc({ id })}>
                                <IconButton>
                                    <EditIcon />
                                </IconButton>
                            </Link>
                            <IconButton onClick={handleOpenDeleteModal}>
                                <DeleteIcon />
                            </IconButton>
                        </Box>
                    </Box>
                    <Stack spacing={7} className="mb-20">
                        <Box className="mt-20">
                            <Grid container spacing={5}>
                                <Grid item xs={12} md={6}>
                                    <label className="mb-5 font-bold">Merchant Category Code</label>
                                    <Typography
                                        variant="h3"
                                        className="mt-20 font-normal black-200"
                                    >
                                        {mcc?.code}
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <label className="mb-5 font-bold">Risk Classification</label>
                                    <Box className="mt-30">
                                        <RiskBlock risk="DEFAULT" />
                                        <RiskBlock risk={mcc?.risk} />
                                    </Box>
                                </Grid>
                            </Grid>
                        </Box>
                        <Box>
                            <Grid container spacing={5}>
                                <Grid item xs={12} md={6}>
                                    <label className="mb-5 font-bold">MCC Friendly Name</label>
                                    <Typography
                                        variant="h3"
                                        className="mt-20 font-normal black-200"
                                    >
                                        {mcc?.name}
                                    </Typography>
                                </Grid>
                            </Grid>
                        </Box>
                        <Box className="mb-40">
                            <Grid container spacing={5}>
                                <Grid item xs={12} md={6}>
                                    <label className="mb-5 font-bold">MCC Description</label>
                                    <Typography
                                        variant="h3"
                                        className="mt-20 font-normal black-200"
                                    >
                                        {mcc && mcc?.formattedDescription ? (
                                            <TextEditorValues data={mcc?.formattedDescription} />
                                        ) : (
                                            <span>{mcc?.description}</span>
                                        )}
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <label className="mb-5 font-bold">Supporting Documents</label>
                                    <Typography
                                        variant="h3"
                                        className="mt-20 font-normal black-200"
                                    >
                                        {mcc && mcc?.formattedDocuments ? (
                                            <TextEditorValues data={mcc?.formattedDocuments} />
                                        ) : (
                                            <span>{mcc?.documents}</span>
                                        )}
                                    </Typography>
                                </Grid>
                            </Grid>
                        </Box>
                    </Stack>
                </MainCard>
            </Stack>
            <Dialog
                open={openDeleteModal}
                onClose={handleCloseDeleteModal}
                aria-labelledby="delete-user-dialog-title"
                sx={{
                    '& .MuiDialog-paper': {
                        width: '400px',
                    },
                }}
            >
                <DialogContent>
                    <ModalContent
                        handleClose={handleCloseDeleteModal}
                        handleAction={handleDeleteRecord}
                        header="Delete MCC?"
                        message="Are you sure you want to permanently delete this MCC?"
                        buttonText="Delete"
                    />
                </DialogContent>
            </Dialog>
        </PageContent>
    );
};

export default ViewMccPage;
//Number of blocks per restriction
export const RiskBlock = ({ risk }: { risk: string }) => {
    const mapRiskClassification = {
        LOW: new Array(1).fill('#32D583'),
        MEDIUM: new Array(2).fill('#F79009'),
        HIGH: new Array(3).fill('#F06C62'),
        PROHIBITED: new Array(5).fill('#B42318'),
        RESTRICTED: new Array(4).fill('#F04438'),
        DEFAULT: new Array(5).fill('#DADADA'),
    };
    return (
        <Stack spacing={1}>
            <Box sx={{ display: 'flex', width: '365px', position: 'absolute' }}>
                {mapRiskClassification[risk].map((bgColor: string, index: number) => (
                    <Box
                        key={index}
                        sx={{
                            width: '70px',
                            height: '8px',
                            bgcolor: `${bgColor}`,
                            borderRadius: '4px',
                            marginRight: '3px',
                        }}
                    ></Box>
                ))}
            </Box>
            {risk && risk !== 'DEFAULT' && (
                <Box>
                    <label
                        style={{
                            color: `${mapRiskClassification[risk][0]}`,
                            textTransform: 'capitalize',
                        }}
                    >
                        {risk.toLowerCase()}
                    </label>
                </Box>
            )}
        </Stack>
    );
};
